import * as React from 'react'
import {
  IonButton,
  IonButtons,
  IonContent,
  IonDatetime,
  IonHeader,
  IonImg,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonLoading,
  IonPage,
  IonToolbar,
  useIonAlert,
  useIonModal,
} from '@ionic/react'
import { ResourceType } from '@capawesome/capacitor-cloudinary'
import { formatDistanceToNow, formatISO, parseISO } from 'date-fns'
import { Listbox } from '@headlessui/react'
import clsx from 'clsx'

import { useToast } from '../../contexts/toastContext'
import { useInfiniteData } from '../../hooks/use-infinite-data'
import { ayrsharePlatforms } from '../../ayrshare/ayrshare-platforms'
import { useDefaultAdminSocialConnections } from '../../ayrshare/queries'
import { OneshopLogo } from '../../ayrshare/social-logos'
import PostHeader from '../../social-sharing/posts/components/post-header'
import { usePostsForApproval } from '../../social-sharing/posts/queries'
import {
  useApprovePost,
  useDenyPost,
  useSchedulePost,
} from '../../social-sharing/posts/mutations'
import { useSocialChannels } from '../../social-sharing/use-social-channels'
import CldVideoPlayer from '../../components/cloudinary/cld-video-player'
import ExpandableText from '../../components/ui/expandable-text'

import type { OverlayEventDetail } from '@ionic/core'
import type { PostOverview } from '../../social-sharing/posts/posts.schema'

type StyledButtonProps = Omit<
  React.ComponentPropsWithoutRef<'button'>,
  'className'
>

interface ApprovalButtonProps extends StyledButtonProps {
  children: React.ReactNode
  danger?: boolean
}

function ApprovalButton({ children, danger, ...props }: ApprovalButtonProps) {
  return (
    <button
      className={clsx([
        'rounded-full px-8 py-2 text-sm font-semibold',
        danger
          ? 'bg-ion-color-danger text-white'
          : 'text-ion-color-primary bg-white',
      ])}
      {...props}
    >
      {children}
    </button>
  )
}

function ScheduleModal({
  onDismiss,
}: {
  onDismiss: (data?: string | null, role?: string) => void
}) {
  const [scheduleDate, setScheduleDate] = React.useState('')

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton
              color="secondary"
              onClick={() => onDismiss(null, 'cancel')}
            >
              Cancel
            </IonButton>
          </IonButtons>

          <IonButtons slot="end">
            <IonButton
              color="yellow"
              disabled={!scheduleDate}
              onClick={() => {
                onDismiss(scheduleDate, 'confirm')
              }}
            >
              Confirm
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding space-y-3">
        <h1 className="text-center text-xl">Schedule and Approve This Post</h1>
        <p>
          This post will be approved and scheduled when you confirm. If this not
          what you wish to do, click Cancel.
        </p>
        <IonDatetime
          style={{
            '--background': 'var(--ion-color-primary)',
            '--background-rgb': '0, 0, 0',
          }}
          value={scheduleDate && formatISO(new Date(scheduleDate))} // convert to date and reformat as ISO otherwise day after is highlighted (related to Z instead of offset hours??)
          color="yellow"
          presentation="date-time"
          min={formatISO(Date.now())}
          onIonChange={(e) => {
            typeof e.detail.value === 'string' &&
              setScheduleDate(e.detail.value)
          }}
        ></IonDatetime>
      </IonContent>
    </IonPage>
  )
}

function ApprovalCard({ post }: { post: PostOverview }) {
  const adminConnectionsQuery = useDefaultAdminSocialConnections()
  const adminConnections = adminConnectionsQuery.data

  const [selectedAdminChannels, updateSelectedAdminChannels] =
    useSocialChannels(adminConnections)

  const [presentAlert] = useIonAlert()
  const [presentModal, dismissModal] = useIonModal(ScheduleModal, {
    onDismiss: (data: string, role: string) => dismissModal(data, role),
  })

  const setToast = useToast()

  const approvePost = useApprovePost()
  const denyPost = useDenyPost()
  const schedulePost = useSchedulePost()

  function validatePost() {
    if (
      post.look.cloudinary?.data?.resource_type !== ResourceType.Video &&
      Object.values(selectedAdminChannels).some((platforms) =>
        platforms.includes('tiktok')
      )
    ) {
      setToast({
        message:
          'You cannot share an image to TikTok. Please deselct that channel.',
      })
      return false
    }

    return true
  }

  function handleSchedulePost() {
    if (validatePost())
      presentModal({
        onDidDismiss: (ev: CustomEvent<OverlayEventDetail<string>>) => {
          if (ev.detail.role === 'confirm') {
            if (!ev.detail.data)
              return setToast({
                message:
                  'A valid date was not chosen, please try schduling again. If the problem persists, contact support.',
                color: 'danger',
              })
            schedulePost.mutate({
              postId: post.objectId,
              date: ev.detail.data,
              accountShare: {
                lookId: post.look.objectId,
                post: post.post,
                accountAyrshares: selectedAdminChannels,
              },
            })
          }
        },
      })
  }

  function handleApprovePost() {
    if (validatePost())
      approvePost.mutate({
        postId: post.objectId,
        accountShare: {
          lookId: post.look.objectId,
          post: post.post,
          accountAyrshares: selectedAdminChannels,
        },
      })
  }

  function handleDenyPost(note: string) {
    denyPost.mutate({
      postId: post.objectId,
      note,
    })
  }

  return (
    <article
      key={post.objectId}
      className="pb-6 sm:rounded-lg sm:rounded-b-none sm:border sm:border-gray-600"
    >
      <PostHeader user={post.user} />

      {/* media */}
      {post.look.cloudinary.data.resource_type === ResourceType.Video ? (
        <div className="aspect-h-16 aspect-w-9">
          <CldVideoPlayer
            id={post.look.objectId}
            publicId={post.look.cloudinary.data.public_id}
            autoplayMode="on-scroll"
            playsInline
            loop
            muted
          />
        </div>
      ) : (
        <div className="aspect-h-4 aspect-w-3">
          <IonImg
            src={post.look.cloudinary.data.secure_url}
            className="bg-ion-background-color size-full object-cover object-top"
          />
        </div>
      )}
      {/* info */}
      <div className="space-y-3 p-2">
        {/* meta info */}
        <div>
          <div className="leading-tight">
            <ExpandableText characterLimit={60}>{post.post}</ExpandableText>
          </div>
          <div className="text-sm text-gray-400">
            {`${formatDistanceToNow(
              parseISO(post.createdAt.iso)
            )} ago`.toUpperCase()}
          </div>
        </div>

        {/* Social Channels */}
        <h2>Post to Main Accounts</h2>
        {adminConnections
          ?.filter((ayrshare) => ayrshare.type === 'business')
          .map((ayrshareConnection) => (
            <div key={ayrshareConnection.objectId} className="flex space-x-4">
              <Listbox
                multiple
                value={selectedAdminChannels[ayrshareConnection.objectId] ?? []}
                onChange={(e) =>
                  updateSelectedAdminChannels((draft) => {
                    draft[ayrshareConnection.objectId] = e
                  })
                }
              >
                <Listbox.Options static className="flex items-start gap-4">
                  {ayrsharePlatforms.map((connection) => {
                    const Icon = connection.icon
                    const disabledSocialAccount =
                      !ayrshareConnection.activeSocialAccounts?.includes(
                        connection.name
                      )

                    return (
                      <Listbox.Option
                        key={connection.name}
                        value={connection.name}
                        className="ui-selected:opacity-100 ui-not-selected:opacity-30"
                        disabled={disabledSocialAccount}
                      >
                        <Icon />
                      </Listbox.Option>
                    )
                  })}
                </Listbox.Options>
              </Listbox>
            </div>
          ))}

        <h2>Post to Creator's Accounts</h2>
        <div className="flex space-x-4">
          <ul className="flex items-start gap-4">
            {ayrsharePlatforms.map((connection) => {
              const Icon = connection.icon
              return (
                <li
                  key={connection.name}
                  className={
                    post.platforms.includes(connection.name)
                      ? 'opacity-100'
                      : 'opacity-30'
                  }
                >
                  <Icon />
                </li>
              )
            })}
            <li
              className={
                post.platforms.includes('oneshop')
                  ? 'opacity-100'
                  : 'opacity-30'
              }
            >
              <OneshopLogo />
            </li>
          </ul>
        </div>

        {/* buttons */}
        <div className="flex justify-center gap-3">
          <ApprovalButton
            onClick={handleApprovePost}
            disabled={!adminConnections}
          >
            Approve
          </ApprovalButton>
          <ApprovalButton
            disabled={!adminConnections}
            onClick={handleSchedulePost}
          >
            Schedule
          </ApprovalButton>
          <ApprovalButton
            danger
            onClick={() =>
              presentAlert({
                header: 'Share your feedback',
                buttons: [
                  {
                    text: 'Cancel',
                    role: 'cancel',
                  },
                  {
                    text: 'Deny',
                    cssClass: 'text-ion-color-danger',
                    handler: (value) => handleDenyPost(value.note),
                  },
                ],
                inputs: [
                  {
                    name: 'note',
                    label: 'Feedback',
                  },
                ],
                backdropDismiss: false,
              })
            }
            disabled={!adminConnections}
          >
            Deny
          </ApprovalButton>
        </div>
      </div>
    </article>
  )
}

function Approvals() {
  const postsQuery = usePostsForApproval()

  const [ionInfiniteScrollRef, loadMorePosts] = useInfiniteData(
    postsQuery.fetchNextPage
  )

  const flatPosts = postsQuery.data?.pages.flatMap((page) => page.data) ?? []

  if (postsQuery.data)
    return (
      <>
        <div className="mx-auto max-w-xl space-y-6 overflow-hidden">
          <div className="ion-padding-vertical space-y-7">
            {flatPosts.length ? (
              <>
                {flatPosts.map((post) => (
                  <ApprovalCard key={post.objectId} post={post} />
                ))}
              </>
            ) : (
              <div className="ion-padding-horizontal">
                <p>There are no posts that need your approval.</p>
              </div>
            )}
          </div>
        </div>
        <IonInfiniteScroll
          ref={ionInfiniteScrollRef}
          onIonInfinite={loadMorePosts}
          threshold="100px"
          disabled={!postsQuery.hasNextPage}
        >
          <IonInfiniteScrollContent
            loadingSpinner="bubbles"
            loadingText="loading more posts..."
          />
        </IonInfiniteScroll>
      </>
    )

  return <IonLoading isOpen message="Loading posts..." />
}

export default Approvals
